<template>
  <div v-if="locationName === '' || locationName == null || false">
    <div class="row">
      <div class="col-12">
        <h5>Dieser Kunde hat keinen Standort.</h5>
        <p>Sie müssen den Standort auswählen, wenn Sie Maßnahmen hinzufügen oder ändern möchten</p>
      </div>
    </div>
  </div>
  <div v-else>
    <form v-if="isLoaded" @submit.prevent="submitForm" novalidate>
      <div class="row">
        <div class="col-12">
          <h6 class="mb-4">Standort: {{ locationName }}</h6>

          <AuditInfo
              :user-role="currentUserRole"
              :created-by-user-data="formData.createdByUserData"
              :created-at="formData.createdAt"
              :updated-by-user-data="formData.updatedByUserData"
              :updated-at="formData.updatedAt"/>

          <div v-if="
              formData.unemployedId === '' ||
              formData.unemployedId == null ||
               false">
            <h6 class="mb-4" style="color: red">{{ $t('general.error') }}</h6>
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col-lg-8 col-md-12 col-sm-12">
          <SelectVue
              v-model="formData.courseId"
              :options="courseDropdown"
              :required="true"
              label="Bezeichnung"
              placeholder="Leistungsart auswählen"
              :validation="vGroupCoaching$.courseId"
              :messages="vGroupCoaching$.courseId.required.$message"
              @update:modelValue="onChangeCourseId"/>
        </div>
        <div class="col-lg-4 col-md-12 col-sm-12">
          <BaseInput
              v-model="courseActionNumber"
              label="Maßnahmennummer"
              :disabled="true"/>
        </div>
      </div>
      <div class="row">
        <div class="col-12">
          <SelectVue
              v-model="formData.clientId"
              :options="clientDropdown"
              :required="true"
              label="Kostenträger"
              placeholder="Wählen Sie Kostenträger"
              :validation="vGroupCoaching$.clientId"
              :messages="vGroupCoaching$.clientId.required.$message"/>
        </div>
      </div>
      <div class="row">
        <div class="col-lg-6 col-md-12 col-sm-12">
          <div v-if="billingTypeDropdown && billingTypeDropdown.length">
            <SelectVue
                v-model="formData.billingType"
                :options="billingTypeDropdown"
                :required="true"
                label="Art der Abrechnung"
                placeholder="Art der Abrechnung"
                :validation="vGroupCoaching$.billingType"
                :messages="vGroupCoaching$.billingType.required.$message"/>
          </div>

          <div class="text-center">
            <BaseToggle
                v-model="formData.requiresSignature"
                label="Unterschriftspflichtig"/>
          </div>
        </div>

        <div class="col-lg-6 col-md-12 col-sm-12">
          <BaseInput
              v-model="formData.clerk"
              label="Sachbearbeiter"/>

          <div class="text-center">
            <BaseToggle
                v-model="formData.digitalSignature"
                label="Digitale Unterschrift"/>
          </div>
        </div>
      </div>
      <div class="d-flex justify-content-center w-100 mt-3">
        <SaveButton
            :label="$t('general.button.save')"
            :loading="isSubmitting"/>
      </div>
    </form>
  </div>
</template>

<script setup>
import dropdownService from '@/api/Dropdown';
import groupCoachingService from '@/api/Unemployed/GroupCoaching';
import AuditInfo from '@/components/AuditInfo.vue';
import BaseInput from '@/components/base/BaseInput.vue';
import BaseToggle from '@/components/base/BaseToggle.vue';
import SelectVue from '@/components/SelectVue.vue';
import {CloseToast, ErrorToast, SuccessToast} from '@/components/VueToast';
import {getCourseGroupCoachingDropdown} from '@/shared/dropdowns';
import {requiredRule} from '@/shared/validators/validationRules';
import {piniaEnumsStore} from '@/store/piniaEnumsStore';
import {piniaUserStore} from '@/store/piniaUserStore';
import useVuelidate from '@vuelidate/core';
import {computed, defineEmits, defineProps, onMounted, ref} from 'vue';
import {useI18n} from 'vue-i18n';
import SaveButton from '@/components/button/SaveButton.vue';

const piniaEnumsState = piniaEnumsStore();
const getEnums = computed(() => piniaEnumsState.enums);
const piniaUserState = piniaUserStore();
const currentUserRole = computed(() => piniaUserState.currentUser?.user?.roles?.[0]);
const {t} = useI18n();
const courseDropdown = ref([]);
const clientDropdown = ref([]);
const billingTypeDropdown = ref([]);
const courseActionNumber = ref('');

const isLoaded = ref(false);
const isSubmitting = ref(false);
const emit = defineEmits(['update:modelValue', 'update:submitForm']);

const props = defineProps({
  unemployedId: {
    type: Number,
    required: true,
  },
  locationName: {
    type: String,
    required: true,
  },
  locationId: {
    type: Number,
    required: true,
  },
  groupCoachingId: {
    type: Number,
    required: true,
  },
  submitForm: {
    type: Boolean,
    required: true,
    default: false,
  },
});

const formData = ref({
  unemployedId: props.unemployedId, // !!! IMPORTANT
  courseId: null,
  clerk: '',
  billingType: '',
  requiresSignature: true,
  digitalSignature: true,
});

const formRules = ref({
  unemployedId: requiredRule(t),
  courseId: requiredRule(t),
  clientId: requiredRule(t),
  billingType: requiredRule(t),
});

const computedRules = computed(() => formRules.value);
const vGroupCoaching$ = useVuelidate(computedRules, formData);

async function submitForm() {
  vGroupCoaching$.value.$touch();
  if (vGroupCoaching$.value.$invalid) {
    return;
  }

  if (isSubmitting.value) return;
  isSubmitting.value = true;

  try {
    if (props.groupCoachingId) {
      await groupCoachingService.putGroupCoaching(formData.value, +props.groupCoachingId);
    } else {
      await groupCoachingService.postGroupCoaching(formData.value);
    }

    SuccessToast(t('general.toasts.save_success'));
    emit('update:modelValue', false);
    emit('update:submitForm', !props.submitForm);
  } catch (error) {
    await ErrorToast(error);
  } finally {
    isSubmitting.value = false;
  }
}

async function applyGroupCoachingData(data) {
  try {
    const courseId = data.data?.nameOfCoaching.id;
    const clientId = data.data?.client.id;

    formData.value = {
      ...data.data,
      unemployedId: props.unemployedId,
      courseId: courseId,
      clientId: clientId,
    };

    await onChangeCourseId(courseId);
    addClientToDropdown(data.data?.client, clientDropdown);

    // Course may be logically deleted and missing from dropdown - fall back to coaching record
    if (!courseActionNumber.value) {
      courseActionNumber.value = data.data?.nameOfCoaching?.actionNumber || '';
    }
  } catch (error) {
    await ErrorToast(error);
  }
}

function addClientToDropdown(clientData, dropdown) {
  if (clientData?.id) {
    const client = {
      label: clientData.name,
      value: clientData.id,
    };
    const exists = dropdown.value.some(item => item.value === client.value);
    if (!exists) {
      dropdown.value.push(client);
    }
  }
}

async function onChangeCourseId(courseId) {
  courseActionNumber.value = courseDropdown.value.find(item => item.value === courseId)?.actionNumber || '';

  try {
    const {data} = await dropdownService.getCourseGroupCoachingClients(courseId);
    clientDropdown.value = [];
    clientDropdown.value = data.data.map(item => ({
      label: `${item.name}`,
      value: item?.id,
    }));
  } catch (error) {
    await ErrorToast(error);
  }
}

async function getGroupCoachingBillingTypeDropdown() {
  billingTypeDropdown.value = getEnums?.value?.groupCoachingBillingTypes || [];
}

onMounted(async () => {
  CloseToast();

  // Kick off in parallel with the dropdown loads below - it doesn't depend on them.
  const coachingDataPromise = props.groupCoachingId
      ? groupCoachingService.getGroupCoaching(+props.groupCoachingId)
      : null;

  await Promise.all([
    getCourseGroupCoachingDropdown(
        courseDropdown,
        'nameOfCoaching',
        'id',
        props.locationId,
    ),
    getGroupCoachingBillingTypeDropdown(),
  ]);

  if (coachingDataPromise) {
    try {
      const {data} = await coachingDataPromise;
      await applyGroupCoachingData(data);
    } catch (error) {
      await ErrorToast(error);
    }
  }

  isLoaded.value = true;
});


</script>
<style scoped>

</style>